import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Line } from "react-chartjs-2";
import moment from "moment";
import { SingleDatePicker } from "react-dates";
import useService from "../../../Hooks/useService";
import ServiceResult from "../../../UI/ServiceResult/ServiceResult";
import TranscriptService from "../../../../Services/Transcript.service";
import WidgetHeader from "../../../UI/Widget/WidgetHeader";
import Widget from "../../../UI/Widget/Widget";
import MeetingService from "../../../../Services/Meeting.service";
import ChartColorService from "../../../../Services/Shared/ChartColor.service";
import { Periods } from "../../../../Domain/Period/Period.model";
import SuccessScoreService from "../../../../Services/SuccessScore.service";
import useUniqueId from "../../../Hooks/useUniqueId";
import useHasPermission from "../../../Hooks/useHasPermission";
import DeprecatedSelect from "../../../UI/DeprecatedSelect/DeprecatedSelect";
import StatsService from "../../../../Services/Stats.service";
import { AlertWarning } from "../../../UI/StandardAlerts/StandardAlerts";
const GPAChart = ({ transcriptState, }) => {
    const makeId = useUniqueId()[1];
    const [dataState, getData] = useService(StatsService.getTranscriptGpaHistoryData);
    const [period, setPeriod] = React.useState(Periods.Month);
    const [fromDate, setFromDate] = React.useState(null);
    const [fromDateFocused, setFromDateFocused] = React.useState(false);
    const [toDate, setToDate] = React.useState(null);
    const [toDateFocused, setToDateFocused] = React.useState(false);
    React.useEffect(() => {
        if (transcriptState.data && transcriptState.data.id) {
            getData(transcriptState.data.id, period, period === "Day" ? "Latest" : "Avg", "cumulative_gpa", fromDate, toDate);
        }
    }, [fromDate, getData, period, toDate, transcriptState.data]);
    return (_jsxs(Widget, { shadow: true, children: [_jsx(WidgetHeader, { title: "Cumulative GPA", className: "pb-2" }), _jsxs("div", { className: "d-flex flex-wrap px-3", children: [_jsxs("div", { className: `d-flex react-date-single-picker-group react-date-single-picker-h-100 mr-2 mb-2`, 
                        // Height of neighboring react-select
                        style: { height: 36 }, children: [_jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: fromDate && moment(fromDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("fromDate"), placeholder: "date from", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setFromDate(null);
                                        return;
                                    }
                                    setFromDate(date.toDate());
                                    if (toDate && date.isAfter(toDate)) {
                                        setToDate(date.toDate());
                                    }
                                }, focused: fromDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setFromDateFocused(focused), small: true }), _jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: toDate && moment(toDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("toDate"), placeholder: "date to", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setToDate(null);
                                        return;
                                    }
                                    setToDate(date.toDate());
                                    if (fromDate && date.isBefore(fromDate)) {
                                        setFromDate(date.toDate());
                                    }
                                }, focused: toDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setToDateFocused(focused), small: true })] }), _jsx(DeprecatedSelect, { "aria-label": "Period", style: { width: 100 }, value: period, clearable: false, onChange: ({ value }) => setPeriod(value), options: Object.values(Periods).map((p) => ({
                            label: p,
                            value: p,
                        })) })] }), _jsx("div", { className: "pt-2 card-body", children: _jsx("div", { children: _jsx(ServiceResult, { state: dataState, children: (data) => (_jsx("div", { children: _jsx(Line, { height: 300, data: {
                                    datasets: [
                                        Object.assign({ label: "GPA", fill: true, data: data.map((datum) => datum.data) }, ChartColorService.Blue),
                                    ],
                                    labels: data.map((datum) => datum.date),
                                }, options: {
                                    scales: {
                                        xAxes: [
                                            {
                                                bounds: "ticks",
                                                type: "time",
                                                time: {
                                                    unit: period.toLowerCase(),
                                                },
                                            },
                                        ],
                                    },
                                    legend: {
                                        display: false,
                                    },
                                    maintainAspectRatio: false,
                                    responsive: true,
                                    animation: { animateRotate: true },
                                } }) })) }) }) })] }));
};
const CreditsChart = ({ transcriptState, }) => {
    const makeId = useUniqueId()[1];
    const [period, setPeriod] = React.useState(Periods.Month);
    const [fromDate, setFromDate] = React.useState(null);
    const [fromDateFocused, setFromDateFocused] = React.useState(false);
    const [toDate, setToDate] = React.useState(null);
    const [toDateFocused, setToDateFocused] = React.useState(false);
    const [attemptedCreditState, getAttemptedCredits] = useService(StatsService.getTranscriptGpaHistoryData);
    const [earnedCreditsState, getEarnedCredits] = useService(StatsService.getTranscriptGpaHistoryData);
    React.useEffect(() => {
        if (transcriptState.data) {
            const transcriptId = transcriptState.data.id;
            getAttemptedCredits(transcriptId, period, period === "Day" ? "Latest" : "Avg", "cumulative_gpa_credits", fromDate, toDate);
            getEarnedCredits(transcriptId, period, period === "Day" ? "Latest" : "Avg", "cumulative_credit_hours_earned", fromDate, toDate);
        }
    }, [
        fromDate,
        getAttemptedCredits,
        getEarnedCredits,
        period,
        toDate,
        transcriptState.data,
    ]);
    return (_jsxs(Widget, { shadow: true, children: [_jsx(WidgetHeader, { title: "Credits", className: "pb-2" }), _jsxs("div", { className: "px-3 d-flex flex-wrap", children: [_jsxs("div", { className: `d-flex react-date-single-picker-group react-date-single-picker-h-100 mr-2 mb-2`, 
                        // Height of neighboring react-select
                        style: { height: 36 }, children: [_jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: fromDate && moment(fromDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("fromDate"), placeholder: "date from", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setFromDate(null);
                                        return;
                                    }
                                    setFromDate(date.toDate());
                                    if (toDate && date.isAfter(toDate)) {
                                        setToDate(date.toDate());
                                    }
                                }, focused: fromDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setFromDateFocused(focused), small: true }), _jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: toDate && moment(toDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("toDate"), placeholder: "date to", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setToDate(null);
                                        return;
                                    }
                                    setToDate(date.toDate());
                                    if (fromDate && date.isBefore(fromDate)) {
                                        setFromDate(date.toDate());
                                    }
                                }, focused: toDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setToDateFocused(focused), small: true })] }), _jsx("label", { htmlFor: makeId("period"), className: "sr-only", children: "Period" }), _jsx(DeprecatedSelect, { id: makeId("period"), "aria-label": "Period", style: { width: 100 }, value: period, clearable: false, onChange: ({ value }) => setPeriod(value), options: Object.values(Periods).map((p) => ({
                            label: p,
                            value: p,
                        })) })] }), _jsx("div", { className: "card-body pt-2", children: _jsx("div", { children: _jsx(ServiceResult, { state: attemptedCreditState, children: (attemptCredits) => (_jsx(ServiceResult, { state: earnedCreditsState, children: (earnedCredits) => (_jsx("div", { children: _jsx(Line, { height: 300, data: {
                                        datasets: [
                                            Object.assign(Object.assign({ label: "Attempted", data: attemptCredits
                                                    .filter((datum) => datum.data !== null)
                                                    .map((datum) => ({
                                                    x: datum.date,
                                                    y: datum.data,
                                                })) }, ChartColorService.Gray), { fill: false }),
                                            Object.assign(Object.assign({ label: "Earned", data: earnedCredits
                                                    .filter((datum) => datum.data !== null)
                                                    .map((datum) => ({
                                                    x: datum.date,
                                                    y: datum.data,
                                                })) }, ChartColorService.Green), { fill: false }),
                                        ],
                                    }, options: {
                                        scales: {
                                            xAxes: [
                                                {
                                                    bounds: "ticks",
                                                    type: "time",
                                                    time: {
                                                        unit: period.toLowerCase(),
                                                    },
                                                },
                                            ],
                                        },
                                        legend: {
                                            display: true,
                                            position: "bottom",
                                        },
                                        maintainAspectRatio: false,
                                        responsive: true,
                                        animation: { animateRotate: true },
                                    } }) })) })) }) }) })] }));
};
const AttendanceChart = ({ transcriptState, }) => {
    const makeId = useUniqueId()[1];
    const [dataState, getData] = useService(StatsService.getAttendedAndMissedByTerm);
    const [period, setPeriod] = React.useState(Periods.Month);
    const [fromDate, setFromDate] = React.useState(null);
    const [fromDateFocused, setFromDateFocused] = React.useState(false);
    const [toDate, setToDate] = React.useState(null);
    const [toDateFocused, setToDateFocused] = React.useState(false);
    React.useEffect(() => {
        var _a;
        if ((_a = transcriptState.data) === null || _a === void 0 ? void 0 : _a.id) {
            getData(transcriptState.data.id, period, fromDate, toDate);
        }
    }, [fromDate, getData, period, toDate, transcriptState.data]);
    return (_jsxs(Widget, { shadow: true, children: [_jsx(WidgetHeader, { title: "Attendance", className: "pb-2" }), _jsxs("div", { className: "px-3 d-flex flex-wrap", children: [_jsxs("div", { className: `d-flex react-date-single-picker-group react-date-single-picker-h-100 mr-2 mb-2`, 
                        // Height of neighboring react-select
                        style: { height: 36 }, children: [_jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: fromDate && moment(fromDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("fromDate"), placeholder: "date from", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setFromDate(null);
                                        return;
                                    }
                                    setFromDate(date.toDate());
                                    if (toDate && date.isAfter(toDate)) {
                                        setToDate(date.toDate());
                                    }
                                }, focused: fromDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setFromDateFocused(focused), small: true }), _jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: toDate && moment(toDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("toDate"), placeholder: "date to", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setToDate(null);
                                        return;
                                    }
                                    setToDate(date.toDate());
                                    if (fromDate && date.isBefore(fromDate)) {
                                        setFromDate(date.toDate());
                                    }
                                }, focused: toDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setToDateFocused(focused), small: true })] }), _jsx(DeprecatedSelect, { "aria-label": "Period", style: { width: 100 }, value: period, clearable: false, onChange: ({ value }) => setPeriod(value), options: Object.values(Periods).map((p) => ({
                            label: p,
                            value: p,
                        })) })] }), _jsx("div", { className: "pt-2 card-body", children: _jsx("div", { children: _jsx(ServiceResult, { state: dataState, children: (data) => (_jsx("div", { children: _jsx(Line, { height: 300, data: {
                                    datasets: [
                                        Object.assign({ legendText: `Attended (${data.reduce((sum, datum) => sum + datum.numAttended, 0)})`, label: "Attended", fill: false, data: data.map((datum) => datum.numAttended) }, ChartColorService.Green),
                                        Object.assign({ label: "Missed", legendText: `Missed (${data.reduce((sum, datum) => sum + datum.numMissed, 0)})`, fill: false, data: data.map((datum) => datum.numMissed) }, ChartColorService.Red),
                                    ],
                                    labels: data.map((datum) => datum.date),
                                }, options: {
                                    legend: {
                                        display: true,
                                        position: "bottom",
                                        labels: {
                                            // @ts-expect-error - TS7006 - Parameter 'chart' implicitly has an 'any' type.
                                            generateLabels(chart) {
                                                const datasets = chart.data.datasets;
                                                const { labels } = chart.legend.options;
                                                const usePointStyle = labels.usePointStyle;
                                                // @ts-expect-error - TS7006 - Parameter 'meta' implicitly has an 'any' type.
                                                return chart._getSortedDatasetMetas().map((meta) => {
                                                    const style = meta.controller.getStyle(usePointStyle ? 0 : undefined);
                                                    const borderWidth = style.borderWidth;
                                                    return {
                                                        text: datasets[meta.index].legendText,
                                                        fillStyle: style.backgroundColor,
                                                        lineCap: style.borderCapStyle,
                                                        lineDash: style.borderDash,
                                                        lineDashOffset: style.borderDashOffset,
                                                        lineJoin: style.borderJoinStyle,
                                                        lineWidth: borderWidth,
                                                        strokeStyle: style.borderColor,
                                                        rotation: style.rotation,
                                                        // Below is extra data used for toggling the datasets
                                                        datasetIndex: meta.index,
                                                    };
                                                });
                                            },
                                        },
                                    },
                                    scales: {
                                        xAxes: [
                                            {
                                                bounds: "ticks",
                                                type: "time",
                                                time: {
                                                    unit: period.toLowerCase(),
                                                },
                                            },
                                        ],
                                    },
                                    maintainAspectRatio: false,
                                    responsive: true,
                                    animation: { animateRotate: true },
                                } }) })) }) }) })] }));
};
const AppointmentsChart = ({ studentPersonId, }) => {
    const makeId = useUniqueId()[1];
    const [dataState, getData] = useService(MeetingService.getNumMeetingsScheduledCancelledForStudent);
    const [period, setPeriod] = React.useState(Periods.Month);
    const [fromDate, setFromDate] = React.useState(null);
    const [fromDateFocused, setFromDateFocused] = React.useState(false);
    const [toDate, setToDate] = React.useState(null);
    const [toDateFocused, setToDateFocused] = React.useState(false);
    React.useEffect(() => {
        getData(studentPersonId, period, fromDate, toDate);
    }, [getData, studentPersonId, period, fromDate, toDate]);
    return (_jsxs(Widget, { shadow: true, children: [_jsx(WidgetHeader, { title: "Appointments" }), _jsxs("div", { className: "d-flex flex-wrap px-3", children: [_jsxs("div", { className: `d-flex react-date-single-picker-group react-date-single-picker-h-100 mr-2 mb-2`, 
                        // Height of neighboring react-select
                        style: { height: 36 }, children: [_jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: fromDate && moment(fromDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("fromDate"), placeholder: "date from", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setFromDate(null);
                                        return;
                                    }
                                    setFromDate(date.toDate());
                                    if (toDate && date.isAfter(toDate)) {
                                        setToDate(date.toDate());
                                    }
                                }, focused: fromDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setFromDateFocused(focused), small: true }), _jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: toDate && moment(toDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("toDate"), placeholder: "date to", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setToDate(null);
                                        return;
                                    }
                                    setToDate(date.toDate());
                                    if (fromDate && date.isBefore(fromDate)) {
                                        setFromDate(date.toDate());
                                    }
                                }, focused: toDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setToDateFocused(focused), small: true })] }), _jsx(DeprecatedSelect, { "aria-label": "Period", style: { width: 100 }, value: period, clearable: false, onChange: ({ value }) => setPeriod(value), options: Object.values(Periods).map((p) => ({
                            label: p,
                            value: p,
                        })) })] }), _jsx("div", { className: "card-body px-3", children: _jsx("div", { children: _jsx(ServiceResult, { state: dataState, children: (data) => (_jsx("div", { children: _jsx(Line, { height: 300, data: {
                                    datasets: [
                                        Object.assign({ label: "Scheduled", fill: false, data: data.map((datum) => datum.numScheduled) }, ChartColorService.Green),
                                        Object.assign({ label: "Cancelled", fill: false, data: data.map((datum) => datum.numCancelled) }, ChartColorService.Red),
                                    ],
                                    labels: data.map((datum) => datum.date),
                                }, options: {
                                    scales: {
                                        xAxes: [
                                            {
                                                bounds: "ticks",
                                                type: "time",
                                                time: {
                                                    unit: period.toLowerCase(),
                                                },
                                            },
                                        ],
                                    },
                                    legend: {
                                        display: true,
                                        position: "bottom",
                                    },
                                    maintainAspectRatio: false,
                                    responsive: true,
                                    animation: { animateRotate: true },
                                } }) })) }) }) })] }));
};
const CourseRiskChart = ({ studentAffiliationId, studentPersonId, transcriptState, }) => {
    var _a;
    const makeId = useUniqueId()[1];
    const [academicTermState, loadAcademicTerms] = useService(TranscriptService.getTranscriptAcademicTerms);
    const [term, setTerm] = React.useState();
    const [fromDate, setFromDate] = React.useState(null);
    const [fromDateFocused, setFromDateFocused] = React.useState(false);
    const [toDate, setToDate] = React.useState(null);
    const [toDateFocused, setToDateFocused] = React.useState(false);
    React.useEffect(() => {
        loadAcademicTerms(studentAffiliationId).then((result) => {
            // If there's a current term, select that one, otherwise pick the first one.
            setTerm(result.terms.find((t) => t.current) || result.terms[0]);
        });
    }, [loadAcademicTerms, studentAffiliationId]);
    const [period, setPeriod] = React.useState(Periods.Month);
    const [courseData, getCourseData] = useService(StatsService.getMinCourseRiskHistoryForTranscriptAndTerm);
    React.useEffect(() => {
        if (transcriptState.data && term) {
            getCourseData(transcriptState.data.id, term.id, period, fromDate, toDate);
        }
    }, [
        studentPersonId,
        period,
        transcriptState.data,
        term,
        getCourseData,
        fromDate,
        toDate,
    ]);
    return (_jsxs(Widget, { shadow: true, children: [_jsx(WidgetHeader, { title: "Course Completion Risk", className: "pb-2", subtitle: {
                    content: "The student's course completion risk trend. The most recent course risk displays on the Student Courses tab. When the percentage is low, this translates to a high risk of course completion.",
                } }), _jsxs("div", { className: "d-flex flex-wrap px-3", children: [_jsxs("div", { className: `d-flex react-date-single-picker-group react-date-single-picker-h-100 mr-2 mb-2`, 
                        // Height of neighboring react-select
                        style: { height: 36 }, children: [_jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: fromDate && moment(fromDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("fromDate"), placeholder: "date from", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setFromDate(null);
                                        return;
                                    }
                                    setFromDate(date.toDate());
                                    if (toDate && date.isAfter(toDate)) {
                                        setToDate(date.toDate());
                                    }
                                }, focused: fromDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setFromDateFocused(focused), small: true }), _jsx(SingleDatePicker
                            // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                            , { 
                                // @ts-expect-error - TS2322 - Type 'Moment | null | undefined' is not assignable to type 'Moment | null'.
                                date: toDate && moment(toDate), displayFormat: "M/D/YYYY", numberOfMonths: 1, id: makeId("toDate"), placeholder: "date to", showClearDate: true, onDateChange: (date) => {
                                    if (!date) {
                                        setToDate(null);
                                        return;
                                    }
                                    setToDate(date.toDate());
                                    if (fromDate && date.isBefore(fromDate)) {
                                        setFromDate(date.toDate());
                                    }
                                }, focused: toDateFocused, isOutsideRange: (date) => date.isAfter(), onFocusChange: ({ focused }) => setToDateFocused(focused), small: true })] }), _jsxs("div", { className: "mb-2 mr-2", style: { width: 200 }, children: [_jsx("label", { className: "sr-only", htmlFor: makeId("termControl"), children: "Term:" }), _jsx(DeprecatedSelect, { "aria-label": "Term", id: makeId("termControl"), isLoading: academicTermState.isLoading, options: ((_a = academicTermState.data) === null || _a === void 0 ? void 0 : _a.terms) || [], labelKey: "name", valueKey: "id", value: term, searchable: false, clearable: false, onChange: setTerm })] }), _jsx("div", { className: "mb-2", style: { width: 100 }, children: _jsx(DeprecatedSelect, { "aria-label": "Period", value: period, clearable: false, onChange: ({ value }) => setPeriod(value), options: Object.values(Periods).map((p) => ({
                                label: p,
                                value: p,
                            })) }) })] }), _jsx("div", { className: "card-body pt-2", children: _jsx("div", { children: _jsx(ServiceResult, { state: courseData, children: (data) => (_jsx("div", { children: _jsx(Line, { height: 300, data: {
                                    // Group all the data by transcriptCourseId
                                    datasets: Object.values(data.reduce((sets, datum) => (Object.assign(Object.assign({}, sets), { [datum.transcriptCourseId]: [
                                            ...(sets[datum.transcriptCourseId] || []),
                                            datum,
                                        ] })), {})).map((set, setI) => {
                                        const colors = [
                                            ChartColorService.Blue,
                                            ChartColorService.Purple,
                                            ChartColorService.Gray,
                                            ChartColorService.Pink,
                                            ChartColorService.DarkBlue,
                                        ];
                                        return Object.assign({ 
                                            // $FlowFixMe flow gets confused about mixed type returned by reduce.
                                            label: `${set[0].courseCode} ${set[0].courseName}`, fill: false, 
                                            // $FlowFixMe
                                            // @ts-expect-error - TS7006 - Parameter 'datum' implicitly has an 'any' type.
                                            data: set.map((datum) => ({
                                                x: datum.date,
                                                y: (datum.risk * 100).toFixed(),
                                            })) }, colors[setI % colors.length]);
                                    }),
                                }, options: {
                                    scales: {
                                        xAxes: [
                                            {
                                                bounds: "ticks",
                                                type: "time",
                                                time: {
                                                    unit: period.toLowerCase(),
                                                },
                                            },
                                        ],
                                        yAxes: [
                                            {
                                                ticks: {
                                                    suggestedMax: 100,
                                                    suggestedMin: 0,
                                                    // @ts-expect-error - TS7006 - Parameter 'val' implicitly has an 'any' type.
                                                    callback: (val) => val + "%",
                                                },
                                            },
                                        ],
                                    },
                                    legend: {
                                        display: true,
                                        position: "bottom",
                                    },
                                    maintainAspectRatio: false,
                                    responsive: true,
                                    animation: { animateRotate: true },
                                } }) })) }) }) })] }));
};
const PersistenceRiskChart = ({ studentAffiliationId, studentPersonId, }) => {
    const [persistenceData, getPersistenceData] = useService(StatsService.getAvgPersistenceRiskHistoryForPersonAndTerm);
    React.useEffect(() => {
        getPersistenceData(studentPersonId);
    }, [studentPersonId, getPersistenceData]);
    return (_jsxs(Widget, { shadow: true, children: [_jsx(WidgetHeader, { title: "Persistence Risk", className: "pb-2" }), _jsx("div", { className: "card-body pt-2", children: _jsx("div", { children: _jsx(ServiceResult, { state: persistenceData, children: (data) => (_jsx("div", { children: _jsx(Line, { height: 300, data: {
                                    labels: data.map((datum) => datum.academicCalendarName),
                                    datasets: [
                                        Object.assign({ label: "Persistence Risk", fill: true, data: data.map((datum) => {
                                                return (datum.risk * 100).toFixed();
                                            }) }, ChartColorService.Blue),
                                    ],
                                }, options: {
                                    scales: {
                                        xAxes: [
                                            {
                                                type: "category",
                                                bounds: "ticks",
                                            },
                                        ],
                                        yAxes: [
                                            {
                                                ticks: {
                                                    suggestedMax: 100,
                                                    suggestedMin: 0,
                                                    // @ts-expect-error - TS7006 - Parameter 'val' implicitly has an 'any' type.
                                                    callback: (val) => val + "%",
                                                },
                                            },
                                        ],
                                    },
                                    legend: {
                                        display: false,
                                    },
                                    maintainAspectRatio: false,
                                    responsive: true,
                                    animation: { animateRotate: true },
                                } }) })) }) }) })] }));
};
const AdvisorStudentsStudentAnalyticsTab = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const [transcriptState, getTranscript] = useService(TranscriptService.getTranscriptForStudent);
    React.useEffect(() => {
        getTranscript(props.student.affiliationId);
    }, [getTranscript, props.student.affiliationId]);
    const [canSeeCourseSuccessScore, setCanSeeCourseSuccessScore] = React.useState(false);
    const [canSeeTermSuccessScore, setCanSeeTermSuccessScore] = React.useState(false);
    const canSeeGpa = useHasPermission("CAN_SEE_CUMULATIVE_GPA");
    const canSeeCredits = useHasPermission("CAN_SEE_CREDIT");
    React.useEffect(() => {
        SuccessScoreService.canCurrentUserSeeCourseRisk().then(setCanSeeCourseSuccessScore);
        SuccessScoreService.canCurrentUserSeePersistenceRisk().then(setCanSeeTermSuccessScore);
    }, []);
    return (_jsx("div", { className: "row", children: _jsxs("div", { className: "col", children: [!((_a = transcriptState === null || transcriptState === void 0 ? void 0 : transcriptState.data) === null || _a === void 0 ? void 0 : _a.id) && (_jsx(AlertWarning, { message: "No transcript data found for this person" })), canSeeGpa && ((_b = transcriptState === null || transcriptState === void 0 ? void 0 : transcriptState.data) === null || _b === void 0 ? void 0 : _b.id) && (_jsx(GPAChart
                // @ts-expect-error - TS2322 - Type '{ studentAffiliationId: string; transcriptState: UseServiceState<Transcript>; }' is not assignable to type 'IntrinsicAttributes & { transcriptState: UseServiceState<Transcript>; }'.
                , { 
                    // @ts-expect-error - TS2322 - Type '{ studentAffiliationId: string; transcriptState: UseServiceState<Transcript>; }' is not assignable to type 'IntrinsicAttributes & { transcriptState: UseServiceState<Transcript>; }'.
                    studentAffiliationId: props.student.affiliationId, transcriptState: transcriptState })), canSeeCredits && ((_c = transcriptState === null || transcriptState === void 0 ? void 0 : transcriptState.data) === null || _c === void 0 ? void 0 : _c.id) && (_jsx(CreditsChart
                // @ts-expect-error - TS2322 - Type '{ studentAffiliationId: string; transcriptState: UseServiceState<Transcript>; }' is not assignable to type 'IntrinsicAttributes & { transcriptState: UseServiceState<Transcript>; }'.
                , { 
                    // @ts-expect-error - TS2322 - Type '{ studentAffiliationId: string; transcriptState: UseServiceState<Transcript>; }' is not assignable to type 'IntrinsicAttributes & { transcriptState: UseServiceState<Transcript>; }'.
                    studentAffiliationId: props.student.affiliationId, transcriptState: transcriptState })), canSeeTermSuccessScore && ((_d = transcriptState === null || transcriptState === void 0 ? void 0 : transcriptState.data) === null || _d === void 0 ? void 0 : _d.id) && (_jsx(PersistenceRiskChart, { studentAffiliationId: props.student.affiliationId, studentPersonId: props.student.personId, 
                    // @ts-expect-error - TS2322 - Type '{ studentAffiliationId: string; studentPersonId: string; transcriptState: UseServiceState<Transcript>; }' is not assignable to type 'IntrinsicAttributes & { studentPersonId: string; studentAffiliationId: string; }'.
                    transcriptState: transcriptState })), canSeeCourseSuccessScore && ((_e = transcriptState === null || transcriptState === void 0 ? void 0 : transcriptState.data) === null || _e === void 0 ? void 0 : _e.id) && (_jsx(CourseRiskChart, { studentAffiliationId: props.student.affiliationId, studentPersonId: props.student.personId, transcriptState: transcriptState })), ((_f = transcriptState === null || transcriptState === void 0 ? void 0 : transcriptState.data) === null || _f === void 0 ? void 0 : _f.id) && (_jsx(AttendanceChart, { transcriptState: transcriptState })), _jsx(AppointmentsChart, { studentPersonId: props.student.personId })] }) }));
};
export default AdvisorStudentsStudentAnalyticsTab;
